import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const CustomerContext = createContext(null);

export function CustomerProvider({ children }) {
  // null = verificando | false = não logado | objeto = cliente autenticado
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    // evita 401 no console para visitantes: só consulta a sessão se já houve login neste navegador
    if (!localStorage.getItem("hg_customer_session")) {
      setCustomer(false);
      return;
    }
    api.get("/account/me").then((r) => setCustomer(r.data)).catch(() => {
      localStorage.removeItem("hg_customer_session");
      setCustomer(false);
    });
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/account/login", { email, password });
      localStorage.setItem("hg_customer_session", "1");
      setCustomer(data);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatApiError(err, "Falha no login") };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/account/register", payload);
      localStorage.setItem("hg_customer_session", "1");
      setCustomer(data);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatApiError(err, "Não foi possível criar a conta") };
    }
  };

  const logout = async () => {
    try {
      await api.post("/account/logout");
    } finally {
      localStorage.removeItem("hg_customer_session");
      setCustomer(false);
    }
  };

  // favoritos: na conta quando logado; localStorage para visitantes
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("hg_favs")) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (customer && customer.favorites) setFavorites(customer.favorites);
  }, [customer]);

  const toggleFavorite = async (productId) => {
    if (customer) {
      try {
        const { data } = await api.post(`/account/favorites/${productId}`);
        setFavorites((prev) =>
          data.favorite ? [...prev, productId] : prev.filter((id) => id !== productId)
        );
        return data.favorite;
      } catch {
        return favorites.includes(productId);
      }
    }
    const next = favorites.includes(productId)
      ? favorites.filter((id) => id !== productId)
      : [...favorites, productId];
    setFavorites(next);
    localStorage.setItem("hg_favs", JSON.stringify(next));
    return next.includes(productId);
  };

  return (
    <CustomerContext.Provider value={{ customer, setCustomer, login, register, logout, favorites, toggleFavorite }}>
      {children}
    </CustomerContext.Provider>
  );
}

export const useCustomer = () => useContext(CustomerContext);
